<template>
	<div class="table-wrapper">
		<div
			class="table"
			:class="{ border: props.bordered }"
		>
			<!-- Hide default slot, only use for table columns config -->
			<div class="hidden">
				<slot />
			</div>
			<!-- Render header -->
			<div
				class="header"
				:class="{ hidden: props.hideHeader }"
			>
				<div class="row">
					<div
						class="grid w-full items-center"
						:style="{
							gridTemplateColumns: tableGrids,
							gridTemplateRows: `24px`,
						}"
					>
						<div
							v-if="props.selectable"
							class="mx-auto"
						>
							<Checkbox
								:model-value="allSelected"
								:indeterminate="someSelected"
								hide-details
								@update:model-value="checked => selectAll(checked)"
							/>
						</div>
						<div
							v-for="item in filterHiddenCols(columns, false)"
							:key="item.label"
							class="flex uppercase pl-[16px]"
							:class="{ 'cursor-pointer': item.sortable }"
							@click="() => updateSorter(item)"
						>
							{{ item.label }}
							<Icon
								v-if="item.sortable"
								class="self-center"
								name="arrow_sort"
								:filled="true"
							/>
						</div>
					</div>
				</div>
			</div>

			<!-- Render body -->
			<div v-if="!props.loading">
				<div class="body">
					<div
						v-for="row in sortedData"
						:key="row.index"
					>
						<div
							class="row"
							:class="{
								selected: isRowSelected(row.index),
								'border-none': !!columns.find(column => column.type === 'expand' && expandRows.includes(row.index)),
								'cursor-pointer': !!columns.find(column => column.type === 'expand'),
							}"
							@click="() => onClickRow(row.index)"
						>
							<div
								class="row-grid"
								:style="{
									gridTemplateColumns: tableGrids,
									gridTemplateRows: `56px`,
								}"
							>
								<div
									v-if="props.selectable"
									class="row-select pl-[16px]"
								>
									<Checkbox
										:model-value="isRowSelected(row.index)"
										:hide-details="true"
										@update:model-value="checked => onSelectRow(checked, row)"
										@click.stop
									/>
								</div>
								<div
									v-for="(column, index) in filterHiddenCols(columns, false)"
									:key="index"
									class="pl-[16px]"
								>
									<Icon
										v-if="column.type === 'expand'"
										class="cursor-pointer"
										:name="expandRows.includes(row.index) ? 'md-expand_less' : 'md-expand_more'"
										@click.stop="expandRow(row.index)"
									/>

									<component
										:is="column.render({ index, row, column })"
										v-else
									/>
								</div>
							</div>
						</div>

						<div
							v-if="expandRows.includes(row.index)"
							class="row row-expand"
						>
							<component
								:is="
									expandCol?.render({
										index: row.index,
										row,
										column: columns.find(column => column.type === 'expand'),
									})
								"
								v-if="expandCol"
								class="w-full"
							/>
						</div>
					</div>
				</div>
				<!-- Mobile viewport -->
				<div class="body-mobile">
					<div
						v-for="row in sortedData"
						:key="row.index"
					>
						<div
							class="row"
							:class="{
								selected: isRowSelected(row.index),
								'border-none': !!columns.find(column => column.type === 'expand' && expandRows.includes(row.index)),
								'py-0': !!columns.find(column => column.type === 'expand' && expandRows.includes(row.index)),
							}"
						>
							<div class="cols-wrapper">
								<div class="header">
									<Checkbox
										v-if="props.selectable"
										:model-value="isRowSelected(row.index)"
										hide-details
										@update:model-value="checked => onSelectRow(checked, row)"
									/>
									<div
										v-for="(headerCol, index) in mobileHeaderCols"
										:key="index"
									>
										<component
											:is="headerCol.render({ index: headerCol.mobileConfig.order, row, column: { ...headerCol } })"
										/>
									</div>
								</div>
								<div class="flex items-center">
									<component
										:is="
											mobileControlCol.render({
												index: mobileControlCol.mobileConfig.order,
												row,
												column: { ...mobileControlCol },
											})
										"
										v-if="mobileControlCol"
									/>
									<Icon
										v-if="!!expandCol"
										name="md-expand_more"
										@click="expandRow(row.index)"
									/>
								</div>
							</div>
							<div
								v-for="(sortedCol, index) in mobileSortedCols"
								:key="index"
								class="flex justify-between mb-[8px]"
								:class="{ 'ml-[40px]': !sortedCol.mobileConfig.fluid }"
							>
								<span
									v-if="sortedCol.mobileConfig.showLabel"
									class="uppercase"
									>{{ sortedCol.label }}</span
								>
								<component
									:is="
										sortedCol.render({
											index: sortedCol.mobileConfig.order,
											row,
											column: { ...sortedCol },
										})
									"
								/>
							</div>

							<div
								v-if="expandRows.includes(row.index)"
								class="row row-expand"
							>
								<component
									:is="
										expandCol?.render({
											index: row.index,
											row,
											column: columns.find(column => column.type === 'expand'),
										})
									"
									v-if="!!expandCol"
								/>
							</div>

							<div
								v-for="(attachmentCol, index) in mobileAttachmentCols"
								:key="index"
								class="attachment-col"
							>
								<span
									v-if="attachmentCol.mobileConfig.showLabel"
									class="uppercase"
									>File attached</span
								>
								<component
									:is="
										attachmentCol.render({
											index: attachmentCol.mobileConfig.order,
											row,
											column: { ...attachmentCol },
										})
									"
								/>
							</div>
							<slot name="rowMobile" />
						</div>
					</div>
				</div>
			</div>

			<Loading
				v-else
				class="mx-auto"
			/>
		</div>
		<div class="table-footer">
			<slot name="footer" />
		</div>
	</div>
</template>

<script lang="ts" setup>
import { generateGridColStyle, sortColsByOrder, filterHiddenCols } from "./utils";
import type { ColumnConfig, TableSorter } from "./types";
import Checkbox from "@/components/Checkbox.vue";
import Loading from "@/components/Loading.vue";

interface TableProps {
	hideHeader?: boolean;
	source: Array<{ [key: string]: any }>;
	modelValue?: Array<{ [key: string]: any }>;
	selectable: boolean;
	defaultSorter?: TableSorter;
	bordered?: boolean;
	loading?: boolean;
}
const props = defineProps<TableProps>();

interface TableState {
	data: Array<{ [key: string]: any }>;
	selection: Array<{ [key: string]: any }>;
	sorter: TableProps["defaultSorter"];
}

const state = reactive<TableState>({
	data: [],
	selection: [],
	sorter: undefined,
});

// Provide columns state to descendants
const columns = ref<ColumnConfig[]>([]);
provide("columnsRef", columns);

const emits = defineEmits<{
	(e: "update:modelValue", selection: TableState["selection"]): void;
	(e: "sorterChange", sorter: TableSorter): void;
}>();

const sortedData = ref<TableState["data"]>([]);

onMounted(() => {
	// Indexing raw data
	state.data = props.source.map((item, index) => ({ index, ...item }));
	sortedData.value = state.data;
	state.selection = props?.modelValue || [];
});

// Sorter configs
watch(
	() => props.defaultSorter,
	(value: TableProps["defaultSorter"]) => {
		if (value) {
			state.sorter = { ...value };
		}
	}
);

watch(
	() => state.sorter,
	sorter => {
		if (sorter) {
			const isCusomizedSorter = columns.value.find(column => column.prop === sorter.prop)?.customizedSorter;
			if (isCusomizedSorter) {
				emits("sorterChange", sorter);
			} else {
				sortedData.value = state.data.sort((itemA, itemB) => {
					if (sorter?.order === "asc") {
						return itemA[sorter!.prop].toString().localeCompare(itemB[sorter!.prop].toString());
					} else {
						return itemB[sorter!.prop].toString().localeCompare(itemA[sorter!.prop].toString());
					}
				});
			}
		} else {
			sortedData.value = state.data;
		}
	}
);

const updateSorter = (column: ColumnConfig) => {
	if (!column.sortable || !column.prop) return;
	state.sorter = {
		prop: Array.isArray(column.prop) ? column.prop[0] : column.prop,
		order: state.sorter?.order === "asc" ? "desc" : "asc",
	};
};

// Selection configs
const allSelected = computed(() => state.selection.length > 0 && props.source.length === state.selection.length);
const someSelected = computed(() => state.selection.length > 0 && props.source.length > state.selection.length);
const selectAll = (checked: boolean) => {
	if (checked) {
		state.selection = state.data.map(item => item);
	} else {
		state.selection = [];
	}
	emits("update:modelValue", state.selection);
};
const isRowSelected = (index: number) => !!state.selection.find(item => item.index === index);
const onSelectRow = (checked: boolean, row: { [key: string]: any }) => {
	if (checked) {
		state.selection.push(row);
	} else {
		state.selection = state.selection.filter(selectedRow => selectedRow.index !== row.index);
	}
	emits("update:modelValue", state.selection);
};

// Row configs
const expandRows = ref<number[]>([]);
const expandRow = (index: number) => {
	if (expandRows.value.includes(index)) {
		expandRows.value = expandRows.value.filter(idx => idx !== index);
	} else {
		expandRows.value.push(index);
	}
};

const onClickRow = (index: number) => {
	const rowCanExpand = !!columns.value.find(column => column.type === "expand");
	if (rowCanExpand) expandRow(index);
};

const tableGrids = computed(() => {
	if (props.selectable) {
		return `40px ${generateGridColStyle(filterHiddenCols(columns.value, false))}`;
	} else {
		return `${generateGridColStyle(filterHiddenCols(columns.value, false))}`;
	}
});

const expandCol = computed(() => filterHiddenCols(columns.value, true).filter(column => column.type === "expand")?.[0]);
const mobileHeaderCols = computed(() =>
	filterHiddenCols(columns.value, true)
		.filter(column => column.mobileConfig.isHeader)
		.sort(sortColsByOrder)
);
const mobileControlCol = computed(
	() => filterHiddenCols(columns.value, true).filter(column => column.mobileConfig.type === "control")?.[0]
);
const mobileSortedCols = computed(() =>
	filterHiddenCols(columns.value, true)
		.filter(
			column =>
				column?.type !== "expand" &&
				!column.mobileConfig.isHeader &&
				!column.mobileConfig.isAttachmentCol &&
				column.mobileConfig.type === undefined
		)
		.sort(sortColsByOrder)
);
const mobileAttachmentCols = computed(() =>
	filterHiddenCols(columns.value, true)
		.filter(column => column.mobileConfig.isAttachmentCol)
		.sort(sortColsByOrder)
);
</script>

<style lang="scss" scoped>
.row {
	@apply w-full flex items-center;
	&.selected {
		box-shadow: 0px 0px 8px rgba(12, 124, 230, 0.2);
		@apply bg-blue-50;
	}
	.row-grid {
		@apply grid w-full items-center;
		.row-select {
			@apply mx-auto;
		}
	}
}

.row:hover:not(.row-expand),
.selected:hover {
	box-shadow: 0px 0px 8px rgba(4, 9, 25, 0.2);
}

.row + .row-expand {
	.row {
		@apply border-none #{!important};
	}
}

.table-wrapper {
	@apply mobile:-mx-[16px];

	.table {
		@apply bg-[#ffffff] text-neutral-700 text-[14px] w-full;
		@apply mobile:rounded-[0px];
		&.border {
			@apply border-neutral-100 border-x-[1px] rounded-[4px];
			@apply mobile:rounded-none;
			.row {
				@apply border-neutral-100 border-b-[1px];
			}
		}

		.header {
			.row:hover {
				box-shadow: none !important;
			}
		}

		.body {
			@apply block;
			@apply mobile:hidden;
		}

		.body-mobile {
			@apply hidden mobile:block;
			.row {
				@apply block p-[16px];
				.cols-wrapper {
					@apply flex justify-between mb-[16px];
					.header {
						@apply flex space-x-[16px] justify-between;
					}
				}
				.attachment-col {
					@apply flex justify-between mb-[8px] pt-[16px] border-t-[1px] border-neutral-100;
				}
			}

			.row-expand {
				@apply p-0;
			}
		}

		.header {
			@apply mobile:hidden;
			.row {
				@apply h-[24px] bg-neutral-100;
			}
		}
	}

	.table-footer {
		@apply text-center block w-full;
	}
}
</style>
